<template>
  <div v-if="shouldRender" data-walkthrough-id="report-graphs-main-chart">
    <div
      v-if="pinnedId !== null"
      class="tw-relative"
      :style="{ height: chartHeight }"
      data-testid="pinned-graph-card"
      :data-graph-id="pinnedId"
    >
      <!-- `.sf-button` sets its own `position: relative`, so the placement
           lives on a wrapper rather than on the button. -->
      <span
        v-if="!readOnly"
        v-tooltip="editTooltip"
        class="tw-absolute tw-top-0 tw-right-0 tw-z-10"
      >
        <SfButtonDs
          icon-only
          icon-before="pen"
          variant="tertiary"
          size="sm"
          :disabled="locked"
          :aria-label="I18n.t('report.drawer.metric_actions.edit')"
          data-testid="pinned-graph-card-edit"
          @click="openEditMetric"
        />
      </span>
      <ChartLoadState
        :graph-data="graphData"
        :loading="loading"
        :errored="errored"
        :error-text="I18n.t('report.drawer.pinned.load_error')"
        :hide-x-axis-labels="!store.fullscreen"
        :compact-y-axis="!store.fullscreen"
      />
    </div>
    <div
      v-else
      :style="locked ? undefined : { height: chartHeight }"
      data-testid="pinned-graph-card-empty"
    >
      <MainChartEmptyState :locked="locked" @create="openCreateMetric" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { storeToRefs } from 'pinia'

import ChartLoadState from '@app/application/report/components/chart-load-state.vue'
import MainChartEmptyState from '@app/application/report/components/graphs-drawer/main-chart-empty-state.vue'
import { usePinnedGraphChart } from '@app/application/report/composables/use-pinned-graph-chart'
import { useReportGraphsDrawer } from '@app/application/report/store/use-report-graphs-drawer'
import { PINNED_CHART_HEIGHT_DEFAULT } from '@app/application/report/constants'
import { SfButtonDs } from '@silverfin/ui-components'
import useI18n from '@app/shared/vue/composables/use-I18n'

defineOptions({
  name: 'PinnedGraphCard',
})

interface Props {
  readOnly?: boolean
  chartHeight?: string
}

const props = withDefaults(defineProps<Props>(), {
  readOnly: false,
  chartHeight: PINNED_CHART_HEIGHT_DEFAULT,
})

const I18n = useI18n()

const store = useReportGraphsDrawer()
const {
  pinned,
  reportTemplateId,
  locale,
  locked,
  setupLocked,
  metricRevision,
} = storeToRefs(store)

const pinnedId = computed(() => pinned.value?.id ?? null)

const shouldRender = computed(
  () => pinnedId.value !== null || (!props.readOnly && !setupLocked.value)
)

const { graphData, loading, errored } = usePinnedGraphChart(
  pinnedId,
  reportTemplateId,
  locale,
  metricRevision
)

const editTooltip = computed(() =>
  locked.value
    ? I18n.t('report.drawer.locked_tooltip')
    : I18n.t('report.drawer.metric_actions.edit')
)

function openCreateMetric(): void {
  window.modalCitizen.openModalPortal('reports/create-metric-modal')
}

function openEditMetric(): void {
  if (locked.value || pinnedId.value === null) return
  window.modalCitizen.openModalPortal('reports/create-metric-modal', {
    graphId: pinnedId.value,
  })
}
</script>
